import { zodResolver } from '@hookform/resolvers/zod';
import type { DiscountCapResource } from '@repo/api-client';
import type {
  ActiveShift,
  DesktopCatalogue,
  DesktopPrinter,
  DesktopSales,
  DesktopShift,
  DesktopStock,
  DesktopSync,
  HeldCatalogueItem,
  HeldStock,
  PricedBasket,
  RecordedSale,
  SaleBasketLine,
  StockFreshness,
} from '@repo/desktop-bridge';
import {
  mostOffBasket,
  priceBasket,
  SALE_DISCOUNT_REASON_LABELS,
  SALE_QUANTITY_MAX,
  type SaleDiscount,
  type SalePaymentPart,
} from '@repo/desktop-bridge';
import { Alert, AlertDescription, AlertTitle } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconMinus,
  IconPlus,
  IconPrinterOff,
  IconReceipt,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import type { Clock } from '@/lib/clock';
import { isBarcodeShaped } from '@/lib/field-schemas';
import { formatPaise } from '@/lib/money';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';
import { cashTenderedField, cashTenderedOnRequest } from './cash-given';
import { CashGiven } from './CashGiven';
import { DiscountDialog } from './DiscountDialog';
import { EndShift } from './EndShift';
import {
  cashCarriesNoReference,
  PAYMENT_MODES,
  paymentModeField,
  paymentReferenceField,
  takesPaymentReference,
} from './payment-modes';
import { QuantityField, TYPED_QUANTITY_MAX } from './QuantityField';
import { postToBoard, rateBoardScale } from './rate-board';
import { useScanCapture } from './scan-capture';
import { choseHowTheyPaid, paymentOnRequest, splitPaymentField, splitTotal } from './split-payment';
import { SplitAnswer, SplitPaymentDialog, SplitSummary } from './SplitPaymentDialog';
import { recordedCeiling } from './stock-ceiling';
import {
  describeStockAge,
  describeStockNote,
  formatStockAsOf,
  stockNoteFor,
} from './stock-position';
import { messageForSaleRefusal, TILL_UNAVAILABLE } from './till-messages';
import { TillActions, tillPlate, tillScroll } from './TillFrame';
import { timeSlotFields, timeSlotOnRequest } from './time-slot';
import { TimeSlotFields } from './TimeSlotFields';
import { TodaysSales } from './TodaysSales';

/**
 * Ringing up goods at an outlet counter.
 *
 * **A basket, not an item** — that is the whole difference from `SellAdmission`
 * beside it (CL-401). Admission is one tap, one ticket, one number; a sale is
 * several taps that accumulate into one receipt, one payment and one entry in
 * the books. So this screen has something admission does not: a state a cashier
 * builds up and can get wrong, which is why every line can be adjusted and
 * removed right up until payment is taken, and why nothing is spent — no number,
 * no paper, no queue entry — until they press the one green button.
 *
 * **It works with the line down, and that is the ordinary case rather than the
 * edge one.** Nothing here fetches: the items come from the copy the device
 * holds, the price comes with them, and the sale resolves when it is durably on
 * this machine rather than when the API has heard. There is deliberately no
 * offline banner and no disabled control — a card sale taken during an outage is
 * an ordinary sale, because this system records payments rather than processing
 * them. The only thing an outage changes on this screen is the plain count of
 * writes waiting to send, drawn in the till's own lettering rather than as a
 * warning (`docs/ui-conventions.md`).
 *
 * **The reference asymmetry is a fact about the screen, not about the error
 * handler.** A payment reference is permitted only on UPI and card and is
 * refused outright on cash, so the field is not offered when cash is selected
 * and is cleared if the cashier switches to cash after typing one. Discovering
 * that rule from a refusal would mean discovering it hours later in the blocked
 * pile, with the customer long gone.
 *
 * **There is no default payment mode, and that is deliberate.** Cash is the
 * commonest and defaulting to it would save a tap on most sales — but the mode
 * is what Day Close reconciles the drawer against, and a card sale recorded as
 * cash is a drawer that is short by exactly that amount with nothing on the
 * screen having looked wrong at the time. One deliberate tap is cheaper than
 * that reconciliation.
 *
 * **The shop is searched, not browsed, once it stops fitting on one screen.** A
 * wall of buttons is the fastest picker there is right up until it needs a
 * scroll, and then it is the slowest. The field filters on name *and* code, and
 * Enter adds the one thing that matches — which is all a barcode scanner is,
 * since a scanner types a code very fast and presses Enter. There is deliberately
 * no scanner mode, no scanner setting and nothing here that knows a scanner
 * exists.
 *
 * The item buttons are deliberately **not** the accent colour, for the reason
 * `SellAdmission` gives: one primary action per screen. Here the green is spent
 * on Take payment, which is the one thing on this screen that commits anything.
 */

/** The one kind sold over a counter. Admission is printed, not rung up. */
const GOODS_KIND: HeldCatalogueItem['kind'] = 'PRODUCT';

/** No discount at all — what the counter holds until it has read the park's cap (#352). */
const CLOSED_CAP: DiscountCapResource = { maxBasisPoints: 0, maxPaise: 0 };

/**
 * The order the board is read in.
 *
 * Built once at module scope rather than per render: a collator is expensive to
 * construct and this one sorts five hundred names.
 */
const NAME_ORDER = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

/**
 * The number a shop knows an item by: `KC-004` is the Kids Corner's item 4 (#344).
 *
 * Every live code has this shape, at every outlet, so it is read off the shape
 * and never off a prefix. A code that does not fit has no number and takes no
 * part. Unique on a till because the catalogue it holds is one outlet's.
 */
const SHOP_NUMBER = /^[A-Z0-9]+-([0-9]{3})$/;

function shopNumber(code: string): number | undefined {
  const digits = SHOP_NUMBER.exec(code)?.[1];

  return digits === undefined ? undefined : Number(digits);
}

/**
 * How often this screen re-reads the held stock position.
 *
 * **The one poll on this screen, and it is here because the thing it reads
 * changes on its own.** The queue count beside it deliberately has no timer —
 * it moves when this till does something, so it is read at those moments — but
 * an age moves every minute whether or not anybody touches the counter, and a
 * plate that has said "2m ago" since the morning is worse than no plate at all.
 *
 * Two local IPC calls and no network: the main process decides when to ask the
 * API, and this only ever reads what the device already holds. A minute bounds
 * how wrong the age on screen can be by a minute.
 */
const STOCK_READ_INTERVAL_MS = 60 * 1000;

/**
 * The payment, as a schema rather than as a handful of checks.
 *
 * `docs/ui-conventions.md`: the schema is the validation, and there is no second
 * way to build a form. The basket itself is not a form — it is built by tapping
 * and holds no free text — so what this covers is the payment: how they paid,
 * and the reference that only two of the three modes may carry.
 *
 * The cash rule is written here as well as being enforced by what the screen
 * draws, and both are worth having: the screen makes it unreachable, and the
 * schema makes it true even if a later change to the screen forgets.
 */
const paymentSchema = z
  .object({
    // Both fragments come from `payment-modes.ts` rather than being spelled here.
    // `SellAdmission` asks the same two questions of a person paying at the gate,
    // and two copies of a `maxLength` or of "Choose how they paid" are two things
    // that drift silently — `docs/ui-conventions.md`, "A rule used by two forms
    // lives in one place".
    //
    // A mode, or a split payment in its place (#357) — never neither.
    paymentMode: paymentModeField.optional(),
    payments: splitPaymentField.optional(),
    paymentReference: paymentReferenceField,

    cashTendered: cashTenderedField,

    // The visitor's window, where the outlet asks (#277) — `time-slot.ts`.
    ...timeSlotFields,
  })
  .refine(choseHowTheyPaid, { path: ['paymentMode'], message: 'Choose how they paid.' })
  // The rule is shared; the sentence is not. A sale and a ticket are different
  // objects to the person reading the refusal.
  .refine(cashCarriesNoReference, {
    path: ['paymentReference'],
    message: 'A cash sale cannot carry a payment reference.',
  });

type PaymentValues = z.infer<typeof paymentSchema>;

/** One line of the basket a cashier is building. Merged by item, never by tap. */
interface BasketLine {
  readonly item: HeldCatalogueItem;
  readonly quantity: number;
  /**
   * Taken past what the records show, on purpose (#295). On this line only: a
   * line rung up afresh, in this basket or the next, starts held again.
   */
  readonly pastRecords?: true;
}

/**
 * How far a line's steppers and typed box go — the recorded level where
 * `recordedCeiling` gives one and the line has not been taken past it, and
 * otherwise the column's ceiling, exactly as before #295.
 *
 * Never below one. A line holds at least the one it was rung up as, so a shelf
 * the records show empty is rung up and then held — the deliberate act is on
 * the line, and there is no line to put it on until the first tap makes one.
 */
const lineLimit = (line: BasketLine, held: HeldStock | null): number => {
  const ceiling = line.pastRecords === true ? null : recordedCeiling(line.item, held);

  return ceiling === null ? SALE_QUANTITY_MAX : Math.max(ceiling, 1);
};

interface Bridge {
  catalogue: DesktopCatalogue;
  sales: DesktopSales;
  /**
   * The slice that can count a shift, and `null` on a shell that cannot.
   *
   * The same split again, with the same fallback: an older shell ends a shift
   * the way it always did — immediately, from the button — and the count screen
   * is not drawn. Losing a report must not cost a cashier the ability to go
   * home.
   */
  takings: DesktopSales | null;
  /**
   * The slice that can list the day's sales, and `null` on a shell that
   * cannot — the same split again, and the counter loses a list rather than a
   * till.
   */
  recent: DesktopSales | null;
  shift: DesktopShift;
  sync: DesktopSync | null;
  /**
   * Asked for `status` alone, and `null` on a shell that does not answer it.
   *
   * The same split `takings` makes and for the same reason: a till whose shell
   * cannot report its printer must still sell, so this screen loses a warning
   * rather than a counter.
   */
  printer: DesktopPrinter | null;
  /**
   * What the records say is on the shelves here, and `null` where nothing can
   * say — off a terminal, or on a shell one release behind this bundle.
   *
   * The same split as `printer` and with the strongest version of its reason:
   * **CL-801 settles that the till warns and never refuses**, so this cannot
   * gate anything and a `null` here costs the counter a warning rather than a
   * counter. A screen that receives one renders exactly as it did before stock
   * existed.
   */
  stock: DesktopStock | null;
}

export function SellGoods({
  bridge,
  shift,
  onShiftEnded,
  timeSlot,
}: {
  bridge: Bridge;
  shift: ActiveShift;
  onShiftEnded: () => void;
  /**
   * Set where this outlet asks for the visitor's time slot (#277), with the
   * clock that prefills it. Absent everywhere else, and then nothing is asked.
   */
  timeSlot?: { clock: Clock } | undefined;
}) {
  const [items, setItems] = useState<readonly HeldCatalogueItem[] | null>(null);
  const [waiting, setWaiting] = useState<number | null>(null);

  /**
   * The basket being built.
   *
   * Merged by item rather than appended per tap: tapping the same product four
   * times is four of it on one line, which is what a customer sees on the paper
   * and what a cashier can correct with one press. The contract permits two
   * lines of one item at different prices — a price superseded between two scans
   * — but this till holds exactly one price per item at a time, so the case
   * cannot arise here and merging is safe.
   */
  const [basket, setBasket] = useState<readonly BasketLine[]>([]);

  /**
   * What the cashier is looking for in the shop.
   *
   * **A wall of buttons is a picker only while it fits on one screen.** Two items
   * are a wall; a gift shop's eighty are a scroll somebody is doing with a
   * customer waiting, and the thing they want is the one they can already name.
   * So the grid is filtered rather than paged or foldered — there is no category
   * on a catalogue item to fold it by, and inventing one here would be inventing
   * a shape the API does not have.
   *
   * Matched against the **code as well as the name**, which is the whole reason
   * Enter does something: a barcode scanner is a keyboard that types a code very
   * fast and presses Enter. Nothing on this screen is scanner-specific and there
   * is no scanner mode to be in — the field is focused when the screen opens, so
   * a scan lands in it and adds the item, and a person typing "tea" gets the same
   * behaviour by the same route.
   */
  const [query, setQuery] = useState('');
  const searchField = useRef<HTMLInputElement>(null);
  const searchFocused = useRef(false);

  /**
   * A scan that matched nothing here, said until the next search or line.
   *
   * **Ordinary, not an error.** Most of the park's goods carry no barcode and
   * never will — Kids Corner, the plant stores, the loose chocolate — so a scan
   * that finds nothing is a cue to pick the item from the board, and the basket
   * is left exactly as it was.
   */
  const [unknownScan, setUnknownScan] = useState<string | null>(null);

  // A scan reaches the field whatever was tapped last (`scan-capture.ts`).
  const beginScan = useCallback((key: string) => {
    setQuery(key);
    setUnknownScan(null);
  }, []);

  useScanCapture(searchField, beginScan);

  /**
   * A basket set aside so the queue can move.
   *
   * **One, and only while the counter is free.** A customer who has gone back to
   * the car for a wallet should not hold up four people, and clearing their
   * basket to serve the next is worse than parking it. But a till that can park
   * any number of baskets is a till with a pile of half-finished sales on it and
   * no way to tell them apart — nothing here is labelled, because nothing here
   * has a customer's name attached. So: park when there is something to park and
   * nothing already parked, resume when the counter is clear.
   *
   * **It does not survive a reload**, and that is honest rather than a gap:
   * nothing was committed — no number, no paper, no queue entry — so a parked
   * basket is a note on a pad, not a record. Persisting it would make a till that
   * has been restarted claim to remember a sale nobody rang up.
   */
  const [parked, setParked] = useState<readonly BasketLine[] | null>(null);

  /**
   * Whether this device can print at all.
   *
   * `null` until asked, and `true` unless the shell says otherwise — a till that
   * cannot answer the question must not be accused. What this catches is the
   * narrow, silent case: **no printer configured on the machine at all**, which
   * is a setup fault that otherwise announces itself only after the first sale,
   * on the notice for a receipt that never came out. The money is in the drawer
   * by then.
   *
   * It cannot catch out of paper, unplugged or jammed, and it must not word
   * itself as though it could: `PrinterStatus` reports the transport, and a
   * transport is configured or not. Those failures still surface where they
   * always did, on `RecordedSale.printed`.
   */
  const [canPrint, setCanPrint] = useState<boolean | null>(null);

  /**
   * The stock position this device was last pushed, and how old it is.
   *
   * **Both, because one without the other is dishonest.** A level with no age
   * implies "now", which this figure has no way to be: it is a snapshot bound at
   * one instant and the outlet has been selling since — from this counter and
   * from every other one at the outlet. So the age is drawn beside every number
   * that comes out of it.
   *
   * `null` is an ordinary answer at every stage — no shell, no position pushed
   * yet, or a call that failed — and none of them stops anything. Nothing on
   * this screen reads these to decide whether a sale may happen.
   */
  const [stock, setStock] = useState<HeldStock | null>(null);
  const [stockAge, setStockAge] = useState<StockFreshness | null>(null);
  const [checkingStock, setCheckingStock] = useState(false);

  /**
   * The park's discount cap from the held catalogue (#352) — closed until it is
   * read, and closed where the held copy has none, so no discount is offered on
   * a guess.
   */
  const [discountCap, setDiscountCap] = useState<DiscountCapResource>(CLOSED_CAP);
  /** The discount on the sale on the counter, and whether its dialog is up. */
  const [discount, setDiscount] = useState<SaleDiscount | null>(null);
  const [discounting, setDiscounting] = useState(false);
  /** Whether the split payment dialog is up (#357). The split itself is on the form. */
  const [splitting, setSplitting] = useState(false);

  /** The last sale, kept on screen until the next one replaces it. */
  const [sold, setSold] = useState<RecordedSale | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  /**
   * Which of the counter's three screens is up.
   *
   * **Held here rather than routed**, because none of the three is a place a
   * till can be sent to: they are all one counter with one shift behind it, and
   * a URL for the shift count would be a URL somebody could reach without a
   * shift. The till's whole gate is the lease and the shift, neither of which
   * the router knows anything about (`Till.tsx`).
   */
  const [view, setView] = useState<'counter' | 'takings' | 'today'>('counter');

  const form = useForm({
    resolver: zodResolver(paymentSchema),
    mode: 'onTouched',
    defaultValues: { paymentReference: '', cashTendered: '' },
  });

  const paymentMode = form.watch('paymentMode');
  /** A split payment in place of a mode (#357), or `undefined`. */
  const payments = form.watch('payments');

  /** Only UPI and card may carry a reference; cash is refused one outright. */
  const takesReference = takesPaymentReference(paymentMode);

  const pending = form.formState.isSubmitting;

  const readQueue = useCallback(() => {
    bridge.sync
      ?.status()
      .then((counts) => {
        setWaiting(counts.pending + counts.inFlight);
      })
      .catch((error: unknown) => {
        // Nothing on screen. The count is a courtesy, and an alarm about a
        // failed IPC call is a worse thing to put in front of a cashier than a
        // missing number.
        console.error('till: the queue could not be read', error);
      });
  }, [bridge.sync]);

  /**
   * Read the position this device holds, with no round trip to the API.
   *
   * The refresh in the main process is what keeps it current — a screen must not
   * fetch on mount, exactly as with the catalogue. What this reads is what the
   * device already has.
   *
   * Deliberately **not** called after a sale, unlike {@link readQueue} beside
   * it. A sale rung up here is in the outbox and may not have reached the server
   * yet, so asking again would spend a request to be told the same figure — and
   * on a till that is selling steadily it would be a request per customer on the
   * connection the offline design exists to survive without.
   */
  const readStock = useCallback(() => {
    const slice = bridge.stock;

    if (slice === null) return;

    void Promise.all([slice.held(), slice.status()])
      .then(([held, status]) => {
        setStock(held);
        setStockAge(status.held);
      })
      .catch((error: unknown) => {
        // Nothing on screen. This is a warning about a shelf, and an alarm about
        // a failed IPC call is a worse thing to put in front of a cashier than a
        // missing one.
        console.error('till: the stock position could not be read', error);
      });
  }, [bridge.stock]);

  /**
   * Ask for a fresh position now.
   *
   * **This is what stops the warning being decoration.** The figure is stale by
   * construction, so "the records show none left" is only useful to somebody who
   * can see how old that is and get a newer one from where they are standing.
   *
   * The outcome is not worded. Every way this can fail — offline, an expired
   * lease, a revoked terminal — leaves the held position exactly where it was
   * and the till selling exactly as it was, and the age below the button is the
   * honest answer to whether it worked: if it did, the age resets; if it did
   * not, it does not. A refusal notice over a counter for a read that gates
   * nothing would be an alarm about a courtesy.
   */
  const checkStock = () => {
    const slice = bridge.stock;

    if (slice === null || checkingStock) return;

    setCheckingStock(true);

    void slice
      .refresh()
      .catch((error: unknown) => {
        console.error('till: the stock position could not be refreshed', error);
      })
      .finally(() => {
        setCheckingStock(false);
        readStock();
      });
  };

  useEffect(() => {
    let live = true;

    // Read from the held copy with no round trip. The version poll in the main
    // process is what keeps it current; a screen must not fetch on mount.
    bridge.catalogue
      .held()
      .then((held) => {
        if (live) {
          setItems(held?.items ?? []);
          setDiscountCap(held?.discountCap ?? CLOSED_CAP);
        }
      })
      .catch((error: unknown) => {
        console.error('till: the catalogue could not be read', error);
        if (live) setItems([]);
      });

    // Once, at the counter opening. The transport is set from the environment at
    // boot and cannot change while the app runs, so polling it would be asking a
    // question whose answer is already known.
    bridge.printer
      ?.status()
      .then((status) => {
        if (live) setCanPrint(status.configured);
      })
      .catch((error: unknown) => {
        // Left `null`. A failed IPC call is not evidence the printer is missing,
        // and a warning drawn on a guess is a warning somebody learns to ignore.
        console.error('till: the printer could not be asked about itself', error);
      });

    readQueue();
    readStock();

    return () => {
      live = false;
    };
  }, [bridge.catalogue, bridge.printer, readQueue, readStock]);

  // Its own effect rather than a timer inside the one above, so the mount reads
  // stay a mount concern and this stays a ticking one.
  useEffect(() => {
    if (bridge.stock === null) return;

    const ticker = setInterval(readStock, STOCK_READ_INTERVAL_MS);

    return () => {
      clearInterval(ticker);
    };
  }, [bridge.stock, readStock]);

  const add = (item: HeldCatalogueItem) => {
    setRefusal(null);
    setUnknownScan(null);

    // The search is spent when it finds something. A cashier who typed "tea" and
    // got it is next looking for something else, and a filter left standing over
    // the grid is a shop that appears to have one item in it.
    setQuery('');

    setBasket((lines) => {
      const existing = lines.find((line) => line.item.id === item.id);

      if (existing === undefined) {
        return [...lines, { item, quantity: 1 }];
      }

      // The column's ceiling, or what the records show (#295). Holding at it
      // rather than refusing the tap: the cashier can see the number stopped,
      // and going past the records is the line's own deliberate act.
      if (existing.quantity >= lineLimit(existing, stock)) {
        return lines;
      }

      return lines.map((line) =>
        line.item.id === item.id ? { ...line, quantity: line.quantity + 1 } : line,
      );
    });
  };

  /** One fewer, and the line goes when the last one does. */
  const removeOne = (itemId: string) => {
    setRefusal(null);

    setBasket((lines) =>
      lines.flatMap((line) => {
        if (line.item.id !== itemId) return [line];

        return line.quantity <= 1 ? [] : [{ ...line, quantity: line.quantity - 1 }];
      }),
    );
  };

  const removeLine = (itemId: string) => {
    setRefusal(null);
    setBasket((lines) => lines.filter((line) => line.item.id !== itemId));
  };

  /**
   * Past what the records show, on purpose (#295) — for this line, and one more
   * on it, which is what the control says it does.
   */
  const sellPastRecords = (itemId: string) => {
    setRefusal(null);
    setBasket((lines) =>
      lines.map((line) =>
        line.item.id === itemId
          ? { ...line, pastRecords: true, quantity: line.quantity + 1 }
          : line,
      ),
    );
  };

  /** A typed quantity (#263) — already a whole number from 1 to the box's ceiling. */
  const setQuantity = (itemId: string, quantity: number) => {
    setRefusal(null);
    setBasket((lines) =>
      lines.map((line) => (line.item.id === itemId ? { ...line, quantity } : line)),
    );
  };

  /**
   * The basket priced, in integer paise — **what will be charged, shown before
   * the money is taken.**
   *
   * A taxed line charges taxable + tax, not the sticker price times the
   * quantity, and a basket with one is taken to the whole rupee (#269): 175 at
   * ₹250 is ₹43,749. So this is `priceBasket`, the one function the main
   * process records the sale with, and not a sum written here — a second copy
   * would be a screen quoting one figure while the paper charges another. The
   * server redoes it all again and refuses a disagreement.
   */
  const pricing = basket.map(({ item, quantity }) => ({ quantity, price: item }));
  const undiscounted = priceBasket(pricing);

  /**
   * The most this bill may have taken off it (#352), and whether the discount
   * on it still fits — a basket that shrank after the discount went on can
   * leave it past the cap. One that does not fit is shown, refused at Take
   * payment, and left off the total rather than quietly cut down.
   */
  const mostOffPaise = undiscounted === null ? 0 : mostOffBasket(discountCap, undiscounted);
  const discountFits = discount !== null && discount.amountPaise <= mostOffPaise;
  const priced = discountFits ? priceBasket(pricing, discount.amountPaise) : undiscounted;
  const totalPaise = priced?.totalPaise ?? 0;

  const takePayment = async (values: PaymentValues) => {
    if (pending) return;

    setRefusal(null);

    if (basket.length === 0) {
      setRefusal(messageForSaleRefusal('SALE_EMPTY_BASKET'));

      return;
    }

    if (discount !== null && !discountFits) {
      setRefusal(messageForSaleRefusal('SALE_DISCOUNT_OVER_CAP'));

      return;
    }

    // A split taken for another total — the basket moved after it was split —
    // is refused here, shown, and left for the cashier to put right, as an
    // over-cap discount is. The main process would refuse it too.
    if (values.payments !== undefined && splitTotal(values.payments) !== totalPaise) {
      setRefusal(messageForSaleRefusal('SALE_PAYMENT_SPLIT_INVALID'));

      return;
    }

    // The items and how many, and nothing else. No amount, no price row, no
    // receipt number — every one of those is decided in the main process from
    // what this device holds, and there is no field here to carry them.
    const lines: SaleBasketLine[] = basket.map((line) => ({
      catalogueItemId: line.item.id,
      quantity: line.quantity,
    }));

    try {
      const outcome = await bridge.sales.record({
        lines,
        // The mode and its reference, exactly as before — the reference omitted
        // rather than sent empty, since `''` is refused where an absent key is
        // accepted — or a split's parts in their place (#357). One spelling of
        // that, shared with the gate.
        ...paymentOnRequest(values),
        // For the paper only: a tax invoice prints what was tendered and the
        // balance — against the cash part, on a split. Never sent to the API.
        ...cashTenderedOnRequest(values, values.cashTendered),
        // For the paper only, where this outlet asks for one.
        ...timeSlotOnRequest(values),
        ...(discount === null ? {} : { discount }),
      });

      if (!outcome.recorded) {
        // The basket is deliberately left standing. Nothing was spent — no
        // number, no paper, no queue entry — so the cashier can fix what the
        // refusal named and take the payment again rather than ringing the whole
        // thing up a second time with a customer waiting.
        setSold(null);
        setRefusal(messageForSaleRefusal(outcome.reason));

        return;
      }

      setSold(outcome.sale);
      // Only now, and only on success: the sale is durably on this device, so
      // the basket it came from is finished with.
      setBasket([]);
      setDiscount(null);
      // The tendered amount goes with the basket it belonged to. Left standing,
      // the next customer's change would be computed off the last one's notes.
      form.reset({ paymentReference: '', cashTendered: '' });
    } catch (error) {
      console.error('till: the sale could not be completed', error);
      setSold(null);
      setRefusal(TILL_UNAVAILABLE);
    } finally {
      // After the sale rather than on a timer: the count changes when something
      // is queued or drained, and a poll running behind a till for a number that
      // is usually zero is not worth the wake-ups on the machine taking money.
      readQueue();
    }
  };

  /**
   * The shop, in the order a board is read.
   *
   * **A rate board on a wall is in an order, and this one was in whatever order
   * the API answered in.** That is invisible at four items and is the difference
   * between a board and a heap at five hundred: the cap below posts the first
   * sixty, and "the first sixty" is only a fair sample of a shop if a person can
   * say where in the shop it stops. By name, they can — it is the beginning of
   * the list, which is what the top of any long list is.
   *
   * `numeric` so *Seed Packet no. 2* precedes *no. 10* rather than following it,
   * and `sensitivity: 'base'` so case and accents do not split a shelf.
   *
   * Memoised on `items` rather than sorted per render: five hundred names
   * through a collator is work that only changes when the catalogue version
   * moves, and everything else on this screen changes on every keystroke.
   */
  const goods = useMemo(
    () =>
      (items ?? [])
        .filter((item) => item.kind === GOODS_KIND)
        .sort((left, right) => NAME_ORDER.compare(left.name, right.name)),
    [items],
  );

  const needle = query.trim().toLowerCase();

  /**
   * The items whose number the query is, when the query is bare digits (#344).
   *
   * By value, so `4`, `04` and `004` all mean `-004`: a cashier types the number
   * the shop's own list gives the item, and that list has no leading zeros.
   */
  const numbered = /^\d+$/.test(needle)
    ? goods.filter((item) => shopNumber(item.code) === Number(needle))
    : [];

  /**
   * Everything the query answers for, which is not the same as what is drawn.
   *
   * **The number goes first and adds to the substring match, never replaces it.**
   * `1` is in a hundred and thirty Kids Corner codes and eighty names; item 1 is
   * the one a cashier who typed it most likely meant, and the rest are still
   * there for the one hunting a name with a 1 in it.
   */
  const matches =
    needle === ''
      ? goods
      : [
          ...numbered,
          ...goods.filter(
            (item) =>
              !numbered.includes(item) &&
              (item.name.toLowerCase().includes(needle) ||
                item.code.toLowerCase().includes(needle) ||
                (item.barcode?.includes(needle) ?? false)),
          ),
        ];

  /**
   * What the board posts, and how much of the shop that is.
   *
   * **The cap is on the plates and never on the search.** `matches` above is the
   * whole answer and is what Enter picks from; this is only what gets drawn, so
   * an item past the sixtieth is still one scan away. `rate-board.ts` carries
   * the number and the measurement behind it.
   */
  const posted = postToBoard(matches);

  /**
   * How big the plates are, given how many the board is holding.
   *
   * Off what is **posted** and not off the shop: a search that narrows forty
   * items to one has put one thing on the board, and that one should get a full
   * plate. Above the cap this is always the dense tier, which is the whole
   * argument for there not being a fourth one.
   */
  const board = rateBoardScale(posted.plates.length);

  /**
   * What Enter means in the search field.
   *
   * **An exact code wins over a single remaining match**, and the order matters:
   * a scanner sends a code, and a code that is also a substring of two names must
   * still add the item it names rather than doing nothing because two things
   * matched. Ambiguity does nothing at all — silence is the right answer when the
   * till cannot tell which of three teas somebody meant, and the grid below is
   * already showing them the three to tap.
   */
  const addBestMatch = () => {
    if (needle === '') return;

    // Matched against the held copy and nothing else (#319). There is no lookup
    // route, and a scan that needed one would fail when the line is down.
    const scanned = goods.find(
      (item) => item.barcode === needle || item.code.toLowerCase() === needle,
    );
    // Off `matches` and deliberately not off what the board posted: the one
    // thing a query leaves is the one thing to add, whether or not the cap drew
    // it. A code that reaches an unposted item is the case the cap exists for.
    const only = matches.length === 1 ? matches[0] : undefined;
    // The item whose number this is, ahead of a lone match but behind a scan or a
    // whole code. Two items with one number is bad data, and does nothing.
    const number = numbered.length === 1 ? numbered[0] : undefined;
    // **Not for a barcode.** A scan is exact or nothing: an unknown EAN-8 can sit
    // inside a known EAN-13, and the one item that "matches" is the wrong one.
    const target = scanned ?? (isBarcodeShaped(needle) ? undefined : (number ?? only));

    // A barcode that is not here is said plainly, and the field is cleared so
    // the next scan is not typed onto the end of this one. Anything else that
    // matched nothing keeps its text, so a typo can be corrected.
    if (target === undefined && isBarcodeShaped(needle)) {
      setUnknownScan(needle);
      setQuery('');
      return;
    }

    if (target === undefined) return;

    add(target);
  };

  /**
   * Focus the search once, when it first appears.
   *
   * Once, not on every render of it: the catalogue is replaced whenever the
   * version moves, and a refocus at that moment would take the caret out of the
   * payment reference somebody was halfway through typing. A scanner needs the
   * field focused when the screen opens and needs nothing after that — Enter
   * leaves the caret where it is.
   */
  useEffect(() => {
    if (searchFocused.current) return;
    if (searchField.current === null) return;

    searchFocused.current = true;
    searchField.current.focus();
  }, [items]);

  /**
   * End the shift and hand the till back.
   *
   * One implementation, reached from the button on a shell that cannot count
   * and from the count screen on one that can — so the two paths cannot end a
   * shift differently. Local like starting one: there is no route for it, and
   * `onShiftEnded` runs whether the call succeeds or not, because a cashier
   * walking away from a till that failed to sign them out is the one outcome
   * worth ruling out.
   */
  const endShift = () => {
    void bridge.shift
      .end()
      .catch((error: unknown) => {
        console.error('till: the shift could not be ended', error);
      })
      .finally(onShiftEnded);
  };

  if (view === 'takings' && bridge.takings !== null) {
    return (
      <EndShift
        sales={bridge.takings}
        shift={shift}
        onBack={() => {
          setView('counter');
        }}
        onEnd={endShift}
      />
    );
  }

  if (view === 'today' && bridge.recent !== null) {
    return (
      <TodaysSales
        sales={bridge.recent}
        onBack={() => {
          setView('counter');
        }}
      />
    );
  }

  return (
    // **A fixed field, not a growing column.** A standing fault that is usually
    // not there, and a body that takes exactly what is left — `min-h-0` on that
    // last one rather than nothing, because a flex item refuses to shrink below
    // its content by default and the overflow escapes to the page, which is the
    // bug this whole layout exists to remove.
    //
    // **`max-w-[84rem]` is gone and nothing replaces it.** It was measured
    // against a frame that no longer exists: the real terminal window is about
    // 1000x625 CSS px, so a 1344px cap never bound once and the void it was
    // meant to solve was vertical the whole time. `docs/design-language.md`
    // records the same number failing the same way on the back office in "The
    // margin and the band"; this is that finding applied one screen over.
    <div className="flex h-full min-h-0 w-full flex-col gap-4">
      {/* **The page-title band is gone — Move 1 of issue #206.** `Sell goods`
          plus *Today's sales* plus *End shift* was the back-office title/action
          band transplanted onto a till, and a cashier knows what screen they are
          on. Both controls are shift-level rather than sale-level, so they sit
          beside the mark, in the zone the frame already gives to who and where.
          `TillActions` is the slot; the reasoning is on it. */}
      <TillActions>
        {/* A plain count in the till's own lettering, never a warning. Items
            waiting is what an outage looks like from here, and an outage is
            what this device is built to keep selling through. */}
        {waiting !== null && waiting > 0 && (
          <span className={tillPlate}>
            <span className="numeric">{waiting}</span> waiting to send
          </span>
        )}

        {/* Absent on a shell that cannot list the day, rather than drawn and
            refusing. The reasoning is in `desktop/bridge.ts`: a cashier cannot interpret a `TypeError`, and
            only one of the two answers leaves the till selling. */}
        {bridge.recent !== null && (
          <Button
            variant="outline"
            size="touch"
            onClick={() => {
              setView('today');
            }}
          >
            <IconReceipt data-icon="inline-start" />
            Today&rsquo;s sales
          </Button>
        )}

        {/* **The count comes first where this shell can produce one.** Ending
            the shift is then confirmed from that screen — the drawer cannot
            be counted against a figure nobody was shown, and a shortfall
            found the next morning in the back office is found by somebody who
            was not standing here.

            Where the shell cannot count, this ends the shift as it always
            did. Losing a report must not cost a cashier the way home. */}
        <Button
          variant="outline"
          size="touch"
          onClick={() => {
            if (bridge.takings === null) {
              endShift();

              return;
            }

            setView('takings');
          }}
        >
          End shift, {shift.displayName}
        </Button>
      </TillActions>

      {/* **Said before the money, not after it.** A till with no printer set up
          records every sale correctly and hands nobody anything, and until now
          the first a cashier heard of it was the notice on a completed sale —
          money in the drawer, customer waiting, nothing to be done from here.

          Not `destructive`: this till is working. It is selling, the sales are
          reaching the office, and what is missing is the paper. A red banner over
          a counter that is functioning is a banner people learn to sell past. */}
      {canPrint === false && (
        <Alert>
          <IconPrinterOff />
          <AlertTitle>No printer is set up on this till</AlertTitle>
          <AlertDescription>
            Sales will be recorded and will reach the office, but nothing will print and no customer
            will get a receipt. Ask the office to set the printer on this machine.
          </AlertDescription>
        </Alert>
      )}

      {items === null ? (
        <ItemsSkeleton />
      ) : goods.length === 0 ? (
        <Alert>
          <IconAlertTriangle />
          <AlertTitle>There is nothing to sell here yet</AlertTitle>
          <AlertDescription>
            This till holds no shop items for its outlet. An owner sets what an outlet sells in the
            back office; once they have, connect this terminal to the park network for a moment to
            pick it up.
          </AlertDescription>
        </Alert>
      ) : (
        // **The sale goes back on the right — issue #217, and it is one change
        // rather than two.** #206 moved it to a strip anchored to the foot
        // because this shop's two items left ~400px of nothing under them while
        // the sale reserved 26rem it was not using. Both voids were real and
        // both were measured against **a catalogue with four items in it**.
        //
        // What the strip cost is only visible on a real outlet's shop: the
        // board and the sale shared one vertical axis, so the sale took the
        // board's height. Measured at 1000x625 with a ten-line basket, the
        // board was **88px** — one row of plates — whatever the shop held, and
        // a 500-item shop was 11,992px of scroll inside it.
        //
        // Side by side, they share the *horizontal* axis instead, and neither
        // can take the other's height at all: the sale grows and shrinks inside
        // its own column, and `Take payment` sits at the foot of that column
        // whatever the basket does. The 0px board is not a state this layout
        // has.
        //
        // **The two voids are answered at both ends of the range and neither is
        // answered by reserving less.** The board's is `rate-board.ts`'s `1fr`
        // row band, unchanged and now with a taller field to grow into. The
        // sale's is the `mt-auto` on its lines: they stand on the total instead
        // of hanging from the top of the column, so one line sits directly above
        // the figure it makes up rather than 250px above it.
        //
        // **The payment questions stay across the foot of the board and do not
        // come into the column**, and that is arithmetic rather than taste —
        // `PaymentRow` carries the three arrangements that were measured and
        // what each one cost. The one thing that must be beside the total is the
        // button, and it is.
        <Form {...form}>
          <div className="flex min-h-0 flex-1 gap-4">
            <section className="flex min-w-0 flex-1 flex-col gap-4">
              <div className="flex shrink-0 flex-wrap items-center gap-x-6 gap-y-3">
                <h2 className={tillPlate}>Shop</h2>

                {/* **Drawn only when something was left off, and then always.**
                    A board that is posting the whole shop needs no count — the
                    plates are the count — but one that is posting sixty of five
                    hundred is making a claim about the shop that is not true,
                    and a cashier who cannot see that it is a sample will scroll
                    to the bottom of it looking for something that was never
                    drawn. In the plate lettering rather than as a warning:
                    nothing here is a fault, and the way to the rest is the
                    field beside it, which says *Search or scan*. */}
                {posted.capped && (
                  <span className={tillPlate}>
                    Showing <span className="numeric">{posted.plates.length}</span> of{' '}
                    <span className="numeric">{posted.held}</span>
                  </span>
                )}

                {/* **The field takes the head's whole width now, and that is the
                    point of the second half of #217.** At four items it was a
                    `sm:max-w-sm` afterthought beside a wall of buttons, because
                    the wall was the picker. At five hundred the wall cannot be,
                    and this is: it matches on code as well as name, Enter adds
                    the best match, and a barcode scanner is a keyboard typing a
                    code very fast into it. */}
                <div className="relative min-w-0 flex-1">
                  <IconSearch
                    aria-hidden
                    className="pointer-events-none absolute top-1/2 left-4 size-5 -translate-y-1/2 text-muted-foreground"
                  />
                  <Input
                    ref={searchField}
                    // `search` rather than `text`: the platform gives it the right
                    // keyboard on a touch panel and the right clearing behaviour.
                    type="search"
                    className={`${fieldSurface} h-touch pl-12 text-base`}
                    // Says what the field takes in the words the counter uses.
                    // "Search products" would be the system's word for it; a
                    // cashier types a name or points a gun at a barcode.
                    placeholder="Search or scan"
                    aria-label="Search the shop by name or code"
                    autoComplete="off"
                    value={query}
                    onChange={(event) => {
                      setQuery(event.target.value);
                      setUnknownScan(null);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        // A bare input is not a form, and a scanner's Enter must
                        // not reach the payment form beside it and take money for
                        // a basket somebody is still building. The payment form is
                        // now a different element in a different column, so this
                        // is belt and braces — and it stays, because the braces
                        // are what a scan is trusted to.
                        event.preventDefault();
                        addBestMatch();
                      }

                      if (event.key === 'Escape') {
                        setQuery('');
                      }
                    }}
                  />
                </div>

                {/* The count is announced rather than drawn: the grid below is
                    already the answer for anybody who can see it, and a number
                    beside the field would be one more thing changing under a
                    cashier's eye on every keystroke. The posted count above is a
                    different fact and is drawn — it is the one thing the plates
                    cannot say for themselves. */}
                <span aria-live="polite" className="sr-only">
                  {needle === ''
                    ? ''
                    : `${String(matches.length)} of ${String(goods.length)} items match`}
                </span>
              </div>

              {/* Below the field it answers, in the board's quiet voice: a scan
                  that finds nothing here is ordinary, and the way on is the
                  board right beneath it. */}
              <p role="status" className="shrink-0 text-base text-muted-foreground empty:hidden">
                {unknownScan !== null && (
                  <>
                    Nothing in this shop carries barcode{' '}
                    <span className="font-mono text-foreground numeric">{unknownScan}</span>. Pick
                    it from the board.
                  </>
                )}
              </p>

              {matches.length === 0 ? (
                // Names what was searched for and offers the way back, at the till
                // scale. This is the one moment somebody actually needs to clear
                // the field — every other path clears it for them — so the control
                // is here, where the problem is, rather than parked inside the
                // input at half the tap target.
                <div className="flex min-h-0 flex-1 flex-col items-start gap-4 py-4">
                  <p className="text-base text-muted-foreground">
                    Nothing in this shop matches{' '}
                    <span className="font-medium text-foreground">{query.trim()}</span>.
                  </p>

                  <Button
                    variant="outline"
                    size="touch"
                    onClick={() => {
                      setQuery('');
                      searchField.current?.focus();
                    }}
                  >
                    Show all {goods.length} items
                  </Button>
                </div>
              ) : (
                // **A rate board that sizes to its stock — Move 2 of issue #206,
                // unchanged and now given a field worth growing into.**
                // `rateBoardScale` moves the track floor and the row band with
                // what is on the board, so few items are few large plates that
                // use the field and forty stay the dense board they already are.
                // The reasoning and the measurements are there.
                //
                // It is keyed on what is **posted**, so a search that narrows
                // five hundred items to one gives that one a full plate, and a
                // board that is at its cap is always the dense tier.
                //
                // **`flex-1` is what makes the row band's `1fr` mean anything.**
                // Without it this is `flex: 0 1 auto`, so the grid is exactly as
                // tall as its content, there is no free space for an `fr` to
                // take, and the plates sit at their floor with mist under them —
                // the void, moved down by one box.
                //
                // **`min-h-[5.5rem]` is a floor the payment row below cannot
                // take**, and it is arithmetic on real content: the row floor
                // every tier states (`5rem`) plus the 4px of ring padding
                // `tillScroll` adds on each edge. A board shorter than one whole
                // plate cuts that plate through its price, which reads as a
                // rendering fault rather than as a board with more on it. It no
                // longer has to survive a ten-line basket — the basket is in the
                // other column and shares none of this height — so it is a third
                // of the `9.5rem` the strip needed.
                <ul
                  className={`grid min-h-[5.5rem] flex-1 ${board.columns} ${board.rows} content-start gap-4 ${tillScroll}`}
                >
                  {posted.plates.map((item) => (
                    <li key={item.id}>
                      <Button
                        variant="outline"
                        // Neutral, not the accent: a wall of green buttons has no
                        // primary action.
                        //
                        // **`h-full`, and that is what makes it a plate rather
                        // than a card.** The row band above decides the height and
                        // the button fills it; without this the grid stretches the
                        // `<li>` and the button sits at the top of a tall empty
                        // cell, which is exactly the void this move exists to
                        // close.
                        //
                        // **`justify-center` and not `justify-between`, and that
                        // was measured too.** `justify-between` put the name on
                        // the plate's top edge and the price on its bottom one,
                        // which reads fine on a 106px plate and, at 1280x800 with
                        // two items, is a 592x317 plate with ~200px of nothing
                        // between two lines of type — the void, moved inside the
                        // box. Board lettering is one block; the plate is what
                        // grows around it.
                        className="h-full min-h-touch w-full flex-col items-start justify-center gap-1 px-5 py-4 text-left whitespace-normal"
                        disabled={pending}
                        onClick={() => {
                          add(item);
                        }}
                      >
                        {/* **A name is whatever somebody typed into the catalogue, and it
                            has to fit.** `whitespace-normal` above wraps at spaces, and a
                            shop item is one long token often enough. `break-words` alone
                            does not do it: this span is a flex item under `items-start`,
                            so it sizes to fit-content, and fit-content's floor is
                            min-content — which `overflow-wrap: break-word` is defined not
                            to reduce. `w-full` is what gives it a definite width to break
                            inside. Measured at 1280x800 with a 39-character name: 5px of
                            horizontal overflow on the grid without this, none with it,
                            and that overflow was half of issue #203. */}
                        <span
                          className={`w-full font-medium break-words text-foreground ${board.name}`}
                        >
                          {item.name}
                        </span>

                        {/* **The price is the loud element**, which is the second
                            half of Move 2. `font-mono` with `numeric` because
                            `docs/design-language.md` reserves mono for "anything
                            read aloud and typed back in", and a price at a counter
                            is read aloud every time. It is the ink colour rather
                            than the muted one now: painted board lettering, and
                            the largest thing on a plate somebody reads from a step
                            back. No new colour and no new face — both are already
                            in the six and the three. */}
                        <span
                          className={`font-mono leading-none font-medium text-foreground numeric ${board.price}`}
                        >
                          {formatPaise(item.sellingPaise)}
                        </span>
                      </Button>
                    </li>
                  ))}
                </ul>
              )}

              <PaymentRow
                form={form}
                takesReference={takesReference}
                totalPaise={totalPaise}
                pending={pending}
                split={payments !== undefined}
                onSplit={
                  basket.length === 0
                    ? null
                    : () => {
                        setSplitting(true);
                      }
                }
                timeSlotClock={timeSlot?.clock ?? null}
                // Park only when there is something to park and the shelf is clear.
                // The control is absent rather than disabled — a greyed Park is a
                // question about why, asked with a customer standing there.
                onPark={
                  basket.length === 0 || parked !== null
                    ? null
                    : () => {
                        setParked(basket);
                        setBasket([]);
                        // ponytail: the discount stays with the counter's sale, not the
                        // parked one — carry it in `parked` if cashiers park discounted bills.
                        setDiscount(null);
                        setRefusal(null);
                        // The parked basket takes its payment with it. It was never
                        // chosen for that basket in any meaningful sense — nobody
                        // picks a mode before the customer is back — and leaving it
                        // set would carry it onto whoever is served next.
                        form.reset({ paymentReference: '', cashTendered: '' });
                      }
                }
                // Absent rather than disabled when the counter is empty, for
                // Park's reason. The shelf is deliberately left alone: Clear is
                // about the sale on the counter, and a parked customer is
                // somebody else's.
                onClear={
                  basket.length === 0
                    ? null
                    : () => {
                        setBasket([]);
                        setDiscount(null);
                        setRefusal(null);
                        // Park's reset and for Park's reason: a tendered amount
                        // left standing would compute the next customer's change
                        // off this one's notes.
                        form.reset({ paymentReference: '', cashTendered: '' });
                      }
                }
              />
            </section>

            <SaleColumn
              form={form}
              lines={basket}
              priced={priced}
              stock={stock}
              stockAge={stockAge}
              checkingStock={checkingStock}
              onCheckStock={bridge.stock === null ? null : checkStock}
              totalPaise={totalPaise}
              pending={pending}
              sold={sold}
              refusal={refusal}
              parked={parked}
              // Resume only when the counter is clear, and absent rather than
              // disabled for the reason Park is.
              onResume={
                parked === null || basket.length > 0
                  ? null
                  : () => {
                      setBasket(parked);
                      setParked(null);
                      setRefusal(null);
                    }
              }
              onAdd={add}
              onQuantity={setQuantity}
              onPastRecords={sellPastRecords}
              onRemoveOne={removeOne}
              onRemoveLine={removeLine}
              onSubmit={takePayment}
              discount={discount}
              discountFits={discountFits}
              mostOffPaise={mostOffPaise}
              onDiscount={() => {
                setDiscounting(true);
              }}
              payments={payments}
            />
          </div>
        </Form>
      )}

      {/* Outside the sale's form, so nothing submitted in here reaches Take payment. */}
      {discounting && (
        <DiscountDialog
          current={discount}
          mostOffPaise={mostOffPaise}
          onApply={(applied) => {
            setDiscount(applied);
            setRefusal(null);
            setDiscounting(false);
          }}
          onRemove={() => {
            setDiscount(null);
            setRefusal(null);
            setDiscounting(false);
          }}
          onClose={() => {
            setDiscounting(false);
          }}
        />
      )}

      {/* Outside the sale's form too, for the same reason (#357). */}
      {splitting && (
        <SplitPaymentDialog
          current={payments ?? null}
          totalPaise={totalPaise}
          onApply={(parts) => {
            // The split takes the mode's place: one or the other answers how
            // they paid, never both. The reference goes with the mode, and the
            // cash given with the cash, where the split has none.
            form.setValue('payments', parts);
            form.setValue('paymentMode', undefined);
            form.setValue('paymentReference', '');
            form.clearErrors(['paymentMode', 'paymentReference']);
            if (!parts.some((part) => part.paymentMode === 'CASH')) {
              form.setValue('cashTendered', '');
            }
            setRefusal(null);
            setSplitting(false);
          }}
          onRemove={() => {
            form.setValue('payments', undefined);
            setRefusal(null);
            setSplitting(false);
          }}
          onClose={() => {
            setSplitting(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * What is being bought, what it comes to, and the one button that commits it.
 *
 * **A column on the right, which is where a cashier watches a sale being
 * built** — issue #217, reversing Move 3 of #206. It reads the way a bill does:
 * the lines, a rule, the total, and the press that spends it. Nothing in it
 * commits anything until that press — no number, no paper, no queue entry —
 * which is what makes every control above the button safe to change with a
 * customer standing there.
 *
 * **The lines stand on the total rather than hanging from the top.** That is
 * the `mt-auto`, and it is the answer to the gulf #206 measured: a one-line
 * basket in a 26rem column left ~250px between the line and the figure it made
 * up. An auto margin takes free space only while there is any — it resolves to
 * zero the moment the lines overflow — so a short basket sits on the rule and a
 * long one scrolls from the top, and neither has a hole in it. It also puts the
 * line a cashier has *just* rung up in the same place every time: immediately
 * above the total, whether it is the first of one or the tenth of ten.
 *
 * **The payment questions are not here**, and `PaymentRow` beside this says why
 * in numbers. What has to be beside the total is the button, and it is.
 */
function SaleColumn({
  form,
  lines,
  priced,
  stock,
  stockAge,
  checkingStock,
  onCheckStock,
  totalPaise,
  pending,
  sold,
  refusal,
  parked,
  onResume,
  onAdd,
  onQuantity,
  onPastRecords,
  onRemoveOne,
  onRemoveLine,
  onSubmit,
  discount,
  discountFits,
  mostOffPaise,
  onDiscount,
  payments,
}: {
  form: ReturnType<typeof useForm<PaymentValues>>;
  lines: readonly BasketLine[];
  /**
   * The same lines, priced — each line's amount by index, and the invoice's
   * round off. `null` only past what a number can hold.
   */
  priced: PricedBasket | null;
  /**
   * What the records say is on the shelves, or `null` where nothing says.
   *
   * **It reaches no control on this panel.** Not `Take payment`, not the
   * steppers, not the item buttons behind it — CL-801: the till warns and it
   * never refuses, so a level can put a sentence beside a line and nothing else.
   */
  stock: HeldStock | null;
  /** How old that position is, for the plate above the lines it annotates. */
  stockAge: StockFreshness | null;
  checkingStock: boolean;
  /** `null` on a shell that cannot answer for stock at all. */
  onCheckStock: (() => void) | null;
  totalPaise: number;
  pending: boolean;
  /** The sale just completed, if one was — shown here, in place of the basket. */
  sold: RecordedSale | null;
  refusal: string | null;
  /** The basket set aside, if one is. */
  parked: readonly BasketLine[] | null;
  /** `null` when there is nothing parked, or the counter is not clear for it. */
  onResume: (() => void) | null;
  onAdd: (item: HeldCatalogueItem) => void;
  onQuantity: (itemId: string, quantity: number) => void;
  onPastRecords: (itemId: string) => void;
  onRemoveOne: (itemId: string) => void;
  onRemoveLine: (itemId: string) => void;
  onSubmit: (values: PaymentValues) => Promise<void>;
  /** The discount on this sale (#352), whether it still fits, and the most that would. */
  discount: SaleDiscount | null;
  discountFits: boolean;
  mostOffPaise: number;
  onDiscount: () => void;
  /** A split payment on this sale (#357), shown on the bill above the total. */
  payments: readonly SalePaymentPart[] | undefined;
}) {
  const parkedTotal =
    priceBasket((parked ?? []).map(({ item, quantity }) => ({ quantity, price: item })))
      ?.totalPaise ?? 0;

  const parkedCount = parked?.reduce((sum, line) => sum + line.quantity, 0) ?? 0;

  /**
   * The lines, scrolled to the one that was just rung up.
   *
   * **The `mt-auto` below only holds while the basket fits.** Under about three
   * lines at 1000x625 the block stands on the total and the newest line is
   * directly above it; past that the region overflows, the auto margin resolves
   * to zero, and the browser leaves the scroll where it was — at the **top**. So
   * a cashier scanning a tenth item watched the first one sit there, which is
   * the opposite of watching a sale being built.
   *
   * Keeping the foot in view is what makes the two cases one behaviour: short or
   * long, the line just added is the line above the total. It is deliberately
   * keyed on the lines and not on a timer or a tap — a basket that changed is
   * the only reason to move somebody's view of it, and a cashier who has
   * scrolled up to check something is left there until they change the sale
   * again.
   *
   * Nothing here focuses anything: moving focus into a scrolling region would
   * take the caret out of the search field, which is where a scanner types.
   */
  const linesRegion = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const region = linesRegion.current;

    if (region === null) return;

    region.scrollTop = region.scrollHeight;
  }, [lines]);

  return (
    // **`w-[22rem]` and `shrink-0`, and the number was measured at both ends.**
    // The line inside it is two rows — name and amount, then the steppers —
    // which needs 312px of content: the three `--size-touch` controls and the
    // remove are 224px of that, and a name gets the rest. Below ~20rem the
    // steppers and the remove no longer stand on one row; above ~26rem the board
    // beside it loses a plate column at 1280x800. 22rem sits between, and at
    // 1000x625 it leaves the board 552px — two dense columns, which is what
    // 1000px affords whatever this number is.
    //
    // **It is not the retired `26rem`, and not because 26 was too wide.** That
    // column reserved a fixed height it did not use; this one reserves a width
    // and fills the height it is given, which is the difference #217 turns on.
    //
    // `aria-label` rather than a heading: the region needs a name for anybody
    // reading it through assistive technology, and a drawn "This sale" plate
    // would be 20px of label above a column whose foot already says `Total` —
    // the head band #206 retired, coming back one word smaller.
    <aside
      aria-label="This sale"
      className="flex w-[22rem] shrink-0 flex-col rounded-2xl border border-border bg-card"
    >
      {/* **The figure's age, at the head of the column whose lines it dates.**
          It used to sit beside the search, where it was a fact about the shop
          filed under the shop. It is a fact about the *notes on these lines* —
          `stockNoteFor` below draws them and this is their provenance — and it
          reads that way directly above them. It also fills the one place a
          bottom-packed column has room to spare, which is how a thing that
          belongs somewhere and a hole that wanted filling met.

          Always drawn where this shell can answer at all, and not only when
          something is wrong: a cashier who can see the position was taken four
          minutes ago reads the warning below differently from one taken before
          lunch, and they cannot do that if the age appears only in the bad case.

          The instant is the park's own clock and the age is on the device's
          effective one, which is the pair `asOf` is for — there is no version to
          poll on this route and there cannot be, so this is the whole of the
          figure's provenance.

          In the till's plate lettering rather than as a warning, like the queue
          count in the frame. Nothing here is a fault. */}
      {onCheckStock !== null && (
        <div className="flex shrink-0 items-center gap-3 px-5 pt-4">
          <span className={`${tillPlate} flex flex-col`}>
            {stockAge?.asOf == null || stockAge.ageMs === null ? (
              // Said rather than left blank. A till that has never been pushed a
              // position warns about nothing, and a cashier should know that is
              // why the shelf notes are absent rather than assuming everything
              // is in stock.
              'No stock figures yet'
            ) : (
              // **Two lines on purpose (#298).** Once the button beside it
              // carried a word the plate had ~106px, and the one-line
              // `Stock 10:51 PM · 1d 4h ago` broke wherever it ran out —
              // "STOCK 10:51 / PM · 1D 4H / AGO". Broken here instead, the
              // instant reads as one unit and the age under it.
              <>
                <span className="whitespace-nowrap">
                  Stock <span className="numeric">{formatStockAsOf(stockAge.asOf)}</span>
                </span>
                <span>{describeStockAge(stockAge.ageMs)}</span>
              </>
            )}
          </span>

          {/* The one control that makes the age worth stating. It commits
              nothing, takes no money and cannot refuse a sale — pressing it
              during an outage leaves everything exactly as it was, which is why
              it is offered rather than hidden when the line is down.

              **A word, and no longer the arrow (#298).** A circular arrow alone
              was named only to a screen reader, and since #295 the figure sets
              where a line's `+` stops, so this is how a cashier whose shelf was
              just restocked lifts that cap now. The word is the button's name,
              so what is read out is what is printed. The arrow went because the
              22rem column could not carry it, the word and the plate's time on
              one line; the word was the half worth keeping, and "Checking…"
              does the spinning arrow's job of saying the press was heard. */}
          <Button
            variant="outline"
            size="touch"
            // `px-5` rather than the touch size's `px-8`: the 24px is what lets
            // `Stock 10:51 PM` stand on one line beside it. Height and floor
            // are untouched.
            className="ms-auto shrink-0 px-5"
            disabled={checkingStock}
            onClick={onCheckStock}
          >
            {checkingStock ? 'Checking…' : 'Check stock'}
          </Button>
        </div>
      )}

      {/* **`overflow-x-hidden`, because otherwise the spec picks the axis and
          does not say so.** Per CSS Overflow 3 an `overflow-y` that is not
          `visible` computes the *other* axis from `visible` to `auto`, so an
          `overflow-y-auto` on its own is a scroll container on both axes with
          the horizontal one appearing nowhere to be read. `till-scroll.test.ts`
          excuses this region from `tillScroll` because its `px-5` already clears
          the focus ring on all four edges, which is true and is why it does not
          take the shared class; the axis is a separate fact and is written down
          here. It cannot be the stricter `clip`: the spec degrades `clip` to
          `hidden` whenever the other axis scrolls.

          **The fade moved to the top edge with the packing.** It is the same
          mask the strip carried, hung the other way round: the lines stand on
          the total and the region is kept scrolled to its foot, so the edge that
          ever runs out of room is the **top** one, and the line clipped by it is
          always one somebody has already rung up. Below three lines there is
          nothing under it to fade — the auto margin has left that space empty —
          so it draws only when it has something to say. */}
      <div
        ref={linesRegion}
        className="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto [mask-image:linear-gradient(to_bottom,transparent,#000_1.5rem)] px-5 py-3"
      >
        {/* **The auto margin is the whole of the anti-void.** It takes the free
            space above the lines while there is free space, and resolves to zero
            the moment there is not — which is what makes it safe to combine with
            a scroll container, where `justify-end` is not: content pushed past
            the start edge of a scroller cannot be scrolled back to. */}
        <div className="mt-auto">
          {/* **The shelf, and only while something is on it.** Stated as goods and
              money rather than as "1 parked sale": what a cashier needs to
              recognise their customer by is what they were buying and what it came
              to, which is all this till knows about them.

              `bg-muted` with `text-foreground` and not the muted pairing — that
              combination is 4.39:1 and misses AA, and it is the one this panel
              invites (`docs/design-language.md`). */}
          {parked !== null && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-muted px-4 py-3">
              <span className="text-sm text-foreground">
                Parked — {parkedCount} {parkedCount === 1 ? 'item' : 'items'},{' '}
                <span className="font-mono numeric">{formatPaise(parkedTotal)}</span>
              </span>

              {onResume === null ? (
                // Said rather than drawn as a disabled button. "Why is this grey"
                // is a question with a customer standing there; the sentence is the
                // answer, and it names the way out.
                <span className="text-sm text-muted-foreground">
                  Finish this sale to bring it back
                </span>
              ) : (
                <Button variant="outline" size="touch" disabled={pending} onClick={onResume}>
                  Bring it back
                </Button>
              )}
            </div>
          )}

          {/* Above the basket rather than above the page: a refusal is about the
              sale being built, and it is left standing beside the lines it
              concerns so the cashier can fix what it names and press again. */}
          {refusal !== null && (
            <Alert variant="destructive" className="mb-4">
              <IconAlertTriangle />
              <AlertDescription>{refusal}</AlertDescription>
            </Alert>
          )}

          {lines.length === 0 && sold !== null ? (
            // **The sale that just finished, where the sale lives.** It used to be
            // a band across the full width of the screen, above everything, which
            // gave a receipt somebody has already been handed the same standing as
            // the counter itself — and pushed the working half of the screen down
            // by its whole height for as long as it stayed. Here it costs nothing:
            // the basket is empty at exactly this moment, so it occupies a space
            // that had nothing in it.
            <SoldNotice sale={sold} />
          ) : lines.length === 0 ? (
            // Says how the first one arrives rather than "empty". It is the state
            // this panel spends most of its day in.
            <p className="text-sm text-muted-foreground">Tap what they are buying.</p>
          ) : (
            <ul className="space-y-4">
              {lines.map((line, index) => {
                /**
                 * What the records say about this line, or `null` for the three
                 * different silences that all mean "nothing to say": no position
                 * has been pushed, the item is not stock-tracked, or the records
                 * cover what is being sold.
                 *
                 * Computed per render rather than held, because it is a pure
                 * function of two things already on screen and a second copy of it
                 * in state is a second thing to keep in step with the basket.
                 */
                const note = stockNoteFor(line.item, line.quantity, stock);

                /**
                 * Where the steppers stop (#295), and whether the line is there
                 * — which is when going past is offered, and not before: a
                 * control beside every line would be a control nobody reads.
                 */
                const ceiling = recordedCeiling(line.item, stock);
                const limit = lineLimit(line, stock);
                const offerPast =
                  ceiling !== null && line.pastRecords !== true && line.quantity >= limit;

                return (
                  <li key={line.item.id} className="space-y-2">
                    {/* **Two rows, which is what a 22rem column buys and what a
                        bill is written as anyway**: what it is and what it comes
                        to on one line, and how many on the next. Across the
                        retired strip it was one row of six controls; in a column
                        the one row would give the name 0px. */}
                    <div className="flex items-baseline gap-3">
                      {/* `break-words` with `flex-1 min-w-0` rather than
                          `truncate`: the strip gave a name ~570px and an ellipsis
                          was the far edge of the case, while 203px here is the
                          ordinary one — a shop item called "Eucalyptus Oil, 200ml"
                          would lose its size. The flex algorithm resolves a
                          definite width for a `flex-1 min-w-0` item, which is what
                          `overflow-wrap` needs to break inside. */}
                      <span className="min-w-0 flex-1 text-base font-medium break-words text-foreground">
                        {line.item.name}
                      </span>

                      {/* Against the column's right edge, where the total under it
                          also lands — `docs/ui-conventions.md` right-aligns amounts
                          so a column can be read down against one edge. Tabular
                          figures so a digit change does not shift the ones beside
                          it. */}
                      <span className="shrink-0 font-mono text-lg text-foreground numeric">
                        {formatPaise(priced?.lines[index]?.amountPaise ?? 0)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="icon-touch"
                        disabled={pending}
                        // The name says what it does to *this* line: a screen reader
                        // hearing six "Remove one" buttons in a row learns nothing.
                        aria-label={`One fewer ${line.item.name}`}
                        onClick={() => {
                          onRemoveOne(line.item.id);
                        }}
                      >
                        <IconMinus />
                      </Button>

                      {/* Typed as well as stepped (#263). Once read-only, for fear
                          of a number fat-fingered by a factor of ten; the box takes
                          three digits and no fourth, which is that fear answered,
                          and a party of 175 is not 175 taps. */}
                      <QuantityField
                        name={line.item.name}
                        quantity={line.quantity}
                        max={Math.min(limit, TYPED_QUANTITY_MAX)}
                        disabled={pending}
                        onQuantity={(quantity) => {
                          onQuantity(line.item.id, quantity);
                        }}
                      />

                      <Button
                        variant="outline"
                        size="icon-touch"
                        disabled={pending || line.quantity >= limit}
                        aria-label={`One more ${line.item.name}`}
                        onClick={() => {
                          onAdd(line.item);
                        }}
                      >
                        <IconPlus />
                      </Button>

                      {/* At the far end of the row, away from the pair it would
                          otherwise be a third of: taking the line off is a
                          different act from taking one off it, and a thumb that
                          misses `−` by 8px must not clear the line. */}
                      <Button
                        variant="outline"
                        size="icon-touch"
                        className="ms-auto"
                        disabled={pending}
                        aria-label={`Take ${line.item.name} off this sale`}
                        onClick={() => {
                          onRemoveLine(line.item.id);
                        }}
                      >
                        <IconTrash />
                      </Button>
                    </div>

                    {/* **A sentence, and never a state on a control.** Nothing above
                        it is disabled by this, nothing below it is, and `Take
                        payment` does not read it; where the steppers stop is
                        `recordedCeiling`'s, and passable (#295). CL-801, and the
                        reasoning is in `stock-position.ts`: a pushed figure is stale by
                        construction, and a till that refuses on one stops the park
                        trading during exactly the outage the offline design exists
                        to survive.

                        Not `destructive` and not the accent. Red on this system
                        means the server refused something or is about to
                        (`docs/ui-conventions.md`), and nothing has been refused
                        here; the emphasis is a step of weight and colour off the
                        muted body, which is what the type scale allows. */}
                    {note !== null && (
                      <p className="flex items-start gap-2 text-sm text-foreground">
                        <IconAlertTriangle aria-hidden className="mt-0.5 size-4 shrink-0" />
                        <span>{describeStockNote(note)}</span>
                      </p>
                    )}

                    {/* The one way past the records, with their figure named at
                        the moment of choosing (#295). Outline and full width:
                        not the screen's primary, and not red — nothing has been
                        refused. */}
                    {offerPast && (
                      <Button
                        variant="outline"
                        size="touch"
                        className="w-full"
                        disabled={pending}
                        onClick={() => {
                          onPastRecords(line.item.id);
                        }}
                      >
                        {`Sell more than the ${String(ceiling)} recorded`}
                      </Button>
                    )}

                    {/* Quiet, beside the warning, so a cashier taking over the
                        counter can see this line went past on purpose. */}
                    {line.pastRecords === true && ceiling !== null && line.quantity > ceiling && (
                      <p className="text-sm text-muted-foreground">Past the records, on purpose.</p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* **The foot of the column, and the one place this screen spends its
          boldness.** Everything below this rule is at a fixed distance from the
          bottom of the counter whatever the basket is doing above it.

          The rule *is* the datum — the two 3px lines the whole system is built
          on — brought inside a panel for the only time in the app. It earns that
          here because it is doing the datum's own job rather than borrowing its
          look: separating what is being counted from what is owed, which is the
          same act of measurement the plates and survey boards it comes from are
          for. A hairline would have been the safe answer and would have read as
          a table rule.

          And the total is the largest thing on the screen, larger than the
          button under it. That is deliberate and it is the risk worth taking: at
          a counter the button is muscle memory within one shift, while the
          number is what gets read aloud, questioned, and counted against the
          drawer at close. Tabular figures so the rupees do not shift under a
          digit change while somebody is reading them out. */}
      <div className="shrink-0 px-5 pt-3 pb-4">
        <div className="h-[3px] w-full datum bg-foreground" />

        {/* **The `<form>` element is here and holds only the total and the
            submit**, while the questions it submits are drawn under the board in
            `PaymentRow`. React Hook Form owns the values through context rather
            than through the DOM, so the two are one form to every part of the
            app that matters — the resolver, the messages and `handleSubmit` are
            unchanged.

            **What the split buys is that the shop is not inside a form.** A
            `<button>` with no `type` inside one is a submit button, which every
            plate on the board would have become: a tap meant to add a rag doll
            would have taken payment for the basket. The board sat outside the
            strip's form by accident of layout before; here it is outside by
            construction, and the same fact makes Enter in the reference field a
            keystroke that does nothing rather than one that takes money — which
            is the rule this screen is built on and the reason the search field
            calls `preventDefault` as well. */}
        <form
          className="flex flex-col gap-3 pt-3"
          noValidate
          onSubmit={(event) => void form.handleSubmit(onSubmit)(event)}
        >
          {/* `flex-wrap` with `ms-auto` rather than `justify-between`: at
              ₹99,999.99 the figure and its label stand on one line, and past
              that the figure drops to a line of its own and stays at the right
              edge instead of pushing the label off the card. */}
          {/* **Only on a tax invoice (#269)**, which is the one basket whose
              lines do not add up to its total: a taxed line carries paise
              (₹43,749.09) and the invoice is taken to the rupee. This is the
              difference, stated where the cashier checks the sum — the paper
              prints the same line. An untaxed basket has none and shows none. */}
          {/* The discount, on the bill as it will print (#352). Past what this
              bill allows — the basket shrank after it went on — it says so in
              the refusal colour, and is left off the total until it is fixed. */}
          {discount !== null && (
            <div
              className={`flex flex-wrap items-baseline gap-x-4 text-sm ${discountFits ? 'text-muted-foreground' : 'text-destructive'}`}
            >
              <span>Discount · {SALE_DISCOUNT_REASON_LABELS[discount.reason]}</span>
              <span className="ms-auto font-mono numeric">
                {discountFits
                  ? `−${formatPaise(discount.amountPaise)}`
                  : `${formatPaise(discount.amountPaise)} — up to ${formatPaise(mostOffPaise)} on this bill`}
              </span>
            </div>
          )}

          {/* The split, on the bill as it was taken (#357) — in the refusal
              colour, saying by how much, once the basket has moved under it. */}
          {payments !== undefined && <SplitSummary payments={payments} totalPaise={totalPaise} />}

          {priced?.invoice != null && (
            <div className="flex items-baseline gap-x-4 text-sm text-muted-foreground">
              <span>Round off</span>
              <span className="ms-auto font-mono numeric">
                {formatPaise(priced.invoice.roundOffPaise)}
              </span>
            </div>
          )}

          <div className="flex flex-wrap items-baseline gap-x-4">
            <span className={tillPlate}>Total</span>
            <span className="ms-auto font-mono text-5xl leading-none font-medium text-foreground numeric">
              {formatPaise(totalPaise)}
            </span>
          </div>

          {/* The one primary action on the screen, and the only control that
              commits anything. Disabled while in flight, which is what stops a
              second receipt for one basket. */}
          {/* Discount shares Take payment's row rather than costing the board one:
              the foot below the board is measured to the pixel at 1000x625. It
              only opens a dialog, so a tap beside the primary action costs
              nothing. */}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="touch"
              className="px-5"
              disabled={pending || lines.length === 0}
              onClick={onDiscount}
            >
              Discount
            </Button>

            <Button
              type="submit"
              size="touch"
              className="flex-1"
              disabled={pending || lines.length === 0}
              // **The amount left the visible label and stayed in the name.** It
              // was in the label because the total was five rows up the old column
              // and the button was the last chance to read it. It is 12px above
              // the button now, at `text-5xl`, and the same figure twice within one
              // eyeful is a redundancy. A screen reader has no "12px above", so it
              // still hears the amount it is about to commit.
              aria-label={pending ? undefined : `Take payment ${formatPaise(totalPaise)}`}
            >
              {pending ? 'Taking payment…' : 'Take payment'}
            </Button>
          </div>
        </form>
      </div>
    </aside>
  );
}

/**
 * How they paid, what they handed over, and the basket set aside — the foot of
 * the board.
 *
 * **This is the half of the sale that is answered once, at the end.** The lines
 * are built up over a minute and belong beside the board being tapped; the mode
 * is one press, the reference is one paste, and the tendered amount is one tap
 * on a note.
 *
 * **The whole of it is 164px and something has to pay for it. This is the
 * cheapest payer, and the alternatives were built and measured rather than
 * argued about.** At 1000x625 the field is 435px and this foot is 552px wide —
 * the modes are 343px and the calculator 430px, so they cannot share a line at
 * any spelling, and the row is two lines whatever is done to it.
 *
 * - **In the sale column, above the total**, where the change figure arguably
 *   belongs: the column is 435px holding a 72px head and a 159px foot, so the
 *   calculator's 138px leaves the basket **38px**. A cashier cannot see the line
 *   they just rang up. Built, rendered, discarded.
 * - **Across the full width, under both**, where the row is one line of 91px:
 *   the payment modes then sit *below* `Take payment`, so the question is asked
 *   under the button that consumes it. Reading order has to end at the press.
 * - **Here**: the board gives 90px while a mode is selected — 258px down to
 *   166px — and the basket column never changes size at all.
 *
 * The board is the right payer because of *when* it is asked to pay. Nothing
 * here is drawn until there is a basket, and a cashier with a basket has
 * finished tapping plates. The board is at its largest exactly while it is being
 * used, and gives way at the moment it stops being.
 *
 * A hairline above it and the board's own width, so it reads as the last row of
 * the shop rather than as a third region.
 */
function PaymentRow({
  form,
  takesReference,
  totalPaise,
  pending,
  split,
  onSplit,
  timeSlotClock,
  onPark,
  onClear,
}: {
  form: ReturnType<typeof useForm<PaymentValues>>;
  takesReference: boolean;
  /** What is owed, for the change calculator. */
  totalPaise: number;
  pending: boolean;
  /** Whether a split payment is the answer (#357), and the press that opens it — `null` with nothing to pay. */
  split: boolean;
  onSplit: (() => void) | null;
  /** Where the outlet asks for a time slot, the clock that prefills it (#277). */
  timeSlotClock: Clock | null;
  /** `null` when there is nothing to park, or somewhere to park it is taken. */
  onPark: (() => void) | null;
  /** `null` when there is nothing on the counter to clear. */
  onClear: (() => void) | null;
}) {
  const [confirmingClear, setConfirmingClear] = useState(false);

  return (
    <div className="shrink-0 border-t border-border pt-4">
      {/* `items-end` so a mode button, an input and a label stand on the same
          line whatever the labels above them do. `flex-wrap` because at 1000x625
          the modes and the calculator are 773px in a 552px foot: wrapping puts
          the calculator on its own line rather than crushing the input to
          nothing — free space in a row is shared by flex-basis, and a `flex-1`
          input beside a `shrink-0` group of keys resolves to 0px. */}
      <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
        {/* **Park is a basket control drawn among payment controls, and that is
            the trade.** It belongs with the lines it sets aside, and there is no
            room for it in the column's head beside the stock plate at 22rem. It
            reads as the first thing in this row rather than as a payment option
            because it is a button among labelled groups, at the far left, with
            the row's own `gap-x-6` between it and "How they paid".

            Absent unless there is a basket to set aside. A cashier who has not
            rung anything up has nothing to park, and a control offered then is
            one more thing on a screen whose whole problem was room. */}
        {/* **Words without icons, and `px-5`, so Clear costs no row (#298).**
            The foot at 1000x625 is 552px and the modes 343px. Park and Clear
            with their icons at the touch size's `px-8` were 268px, which put
            the modes on a line of their own and took 72px from the board; as
            words at `px-5` they and the modes share the line. */}
        {(onPark !== null || onClear !== null) && (
          <div className="flex shrink-0 gap-2">
            {onPark !== null && (
              <Button
                variant="outline"
                size="touch"
                className="px-5"
                disabled={pending}
                onClick={onPark}
              >
                Park
              </Button>
            )}

            {/* **Clear asks once, and that is the one piece of ceremony on this
                row.** Everything else on the till is answered in a tap because
                nothing is lost by a wrong one; this is the control that loses
                the basket, it stands a gap away from Park, and a mis-tap on a
                twenty-line sale is a minute of ringing it up again in front of
                the customer who is still buying it. A deliberate clear costs
                one more tap, and it is rare — a customer changing their mind,
                not every sale. */}
            {onClear !== null && (
              <Button
                variant="outline"
                size="touch"
                className="px-5"
                disabled={pending}
                onClick={() => {
                  setConfirmingClear(true);
                }}
              >
                Clear
              </Button>
            )}
          </div>
        )}

        <Dialog open={confirmingClear && onClear !== null} onOpenChange={setConfirmingClear}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Clear this sale?</DialogTitle>
              <DialogDescription>
                Every line comes off the counter and nothing is charged. A parked sale stays parked.
              </DialogDescription>
            </DialogHeader>

            <DialogFooter>
              <Button
                variant="outline"
                size="touch"
                onClick={() => {
                  setConfirmingClear(false);
                }}
              >
                Keep it
              </Button>
              <Button
                variant="destructive"
                size="touch"
                onClick={() => {
                  setConfirmingClear(false);
                  onClear?.();
                }}
              >
                Clear <span className="font-mono numeric">{formatPaise(totalPaise)}</span>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <FormField
          control={form.control}
          name="paymentMode"
          render={({ field }) => (
            <FormItem className="shrink-0">
              {/* **Deliberately not `FormLabel`**, which is the one place this
                  screen steps outside the form components, and it steps out to be
                  more correct rather than less: `FormLabel` renders a real
                  `<label htmlFor>`, and a radio group has no single control for
                  one to point at — the label would name nothing. A named span the
                  group points back at through `aria-labelledby` is what names a
                  group, and it carries the same lettering and the same required
                  marker the component would have drawn. */}
              <span id={`${field.name}-label`} className={fieldLabel}>
                How they paid
                <span aria-hidden className="text-destructive">
                  *
                </span>
              </span>

              {/* Composed from buttons rather than taken from the registry, which
                  has no radio group or toggle group — and adding one would mean a
                  `shadcn add` that has silently overwritten the button's touch
                  sizes three times. Three targets at the till floor, in the order
                  a counter uses them. */}
              <div
                role="radiogroup"
                aria-labelledby={`${field.name}-label`}
                aria-required
                className="flex gap-2"
              >
                {PAYMENT_MODES.map(({ mode, label }) => (
                  <Button
                    key={mode}
                    type="button"
                    role="radio"
                    aria-checked={field.value === mode}
                    // Selected reads as a filled button and unselected as an
                    // outline, so the chosen mode is legible from a step back —
                    // but `secondary` rather than the accent, because the one
                    // primary action on this screen is Take payment.
                    variant={field.value === mode ? 'secondary' : 'outline'}
                    size="touch"
                    // `px-4`, so four answers take the width three did (#357).
                    className="px-4"
                    disabled={pending}
                    onClick={() => {
                      field.onChange(mode);
                      // A mode is the whole payment: it replaces a split (#357).
                      form.setValue('payments', undefined);

                      // **The asymmetry, handled here rather than in the error
                      // handler.** Switching to cash clears a reference the
                      // cashier had already typed; leaving it would send a cash
                      // sale carrying one, which the API refuses outright
                      // (`SALES_PAYMENT_REFERENCE_NOT_ALLOWED`) rather than
                      // dropping — hours later, in the blocked pile.
                      if (mode === 'CASH') {
                        form.setValue('paymentReference', '', { shouldValidate: false });
                        form.clearErrors('paymentReference');
                      } else {
                        // The mirror of it, for a different reason: the reference
                        // is cleared because the API refuses one, and the tendered
                        // amount is cleared because nothing is handed back on a
                        // card. Leaving it would put a stale change figure one tap
                        // away from the screen.
                        form.setValue('cashTendered', '', { shouldValidate: false });
                        form.clearErrors('cashTendered');
                      }
                    }}
                  >
                    {label}
                  </Button>
                ))}

                <SplitAnswer
                  split={split}
                  disabled={pending || onSplit === null}
                  onSplit={onSplit}
                />
              </div>

              <FormMessage />
            </FormItem>
          )}
        />

        {/* Only once there is something to charge, so each basket is
            prefilled from the clock when its first line goes on. */}
        {timeSlotClock !== null && totalPaise > 0 && (
          <TimeSlotFields clock={timeSlotClock} disabled={pending} />
        )}

        <CashGiven totalPaise={totalPaise} disabled={pending} />

        {/* Not rendered at all on cash, rather than disabled. A disabled field
            still says the park might want one; an absent field says the question
            does not apply, which is the truth. */}
        {takesReference && (
          <FormField
            control={form.control}
            name="paymentReference"
            render={({ field }) => (
              <FormItem className="min-w-56 flex-1">
                <FormLabel className={fieldLabel}>Reference (optional)</FormLabel>
                <FormControl
                  render={
                    <Input
                      className={`${fieldSurface} h-touch font-mono text-lg numeric`}
                      placeholder="431109882314"
                      autoComplete="off"
                      inputMode="numeric"
                      {...field}
                    />
                  }
                />
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
    </div>
  );
}

/**
 * The sale that just happened, and nothing to press on it.
 *
 * The number is the largest thing on it, in mono with tabular figures, because
 * it is read aloud across a counter and typed back into the back office's
 * lookup.
 *
 * **The till sells and does not correct (#294).** This notice used to carry a
 * refund and a reprint, and the park took both away after watching the reprint
 * hand a visitor paper for a sale that was never recorded — the money kept, and
 * nothing in the day's takings to say a sale happened. A refund is the same
 * lever on the drawer. Both are a manager's now, in the back office, from the
 * number on this notice. Today's sales says so; a selling screen does not
 * repeat it, except where the paper did not come out and the cashier has to
 * say something to the visitor in front of them.
 */
function SoldNotice({ sale }: { sale: RecordedSale }) {
  return (
    <Alert variant={sale.printed ? undefined : 'destructive'}>
      {sale.printed ? <IconCircleCheck /> : <IconPrinterOff />}
      <AlertTitle>
        {sale.printed ? 'Sold' : 'Sold, but it did not print'} —{' '}
        <span className="font-mono numeric">{formatPaise(sale.totalPaise)}</span>
      </AlertTitle>
      <AlertDescription>
        <p className="font-mono text-3xl text-foreground numeric">{sale.number}</p>

        {/* The tax invoice's own number, which is what the customer's paper is
            headed with and what an accountant will quote back (#269). Below the
            receipt number and smaller: the receipt number is still what the
            back office looks the sale up by. */}
        {sale.invoiceNumber !== undefined && (
          <p>
            Tax invoice{' '}
            <span className="font-mono text-foreground numeric">{sale.invoiceNumber}</span>
          </p>
        )}

        {!sale.printed && (
          // Said plainly, because there is no way to fix it from here and
          // pretending otherwise would be worse. The sale is recorded — the
          // money was taken and the server will hear about it — so what is
          // missing is the customer's copy rather than the record, and since
          // #294 the copy is a manager's to reprint. A shop sale's paper is a
          // GST tax invoice a visitor may need, so it is worth the walk.
          <p>
            The sale is recorded and will reach the office, but no receipt came out. Check the
            printer. Do not ring this basket up again — it has already been charged. A manager can
            reprint the receipt in the back office from the number above.
          </p>
        )}
      </AlertDescription>
    </Alert>
  );
}

/**
 * The board the skeleton is drawn at.
 *
 * Six slots is what it draws, so it takes the tier six items would get. It is a
 * guess by construction — the catalogue has not been read yet, so nothing here
 * knows how many plates are coming — and a guess in the middle moves least
 * whichever way the answer falls.
 */
const SKELETON_BOARD = rateBoardScale(6);

/** A skeleton in the shape of the buttons, so nothing shifts when they land. */
function ItemsSkeleton() {
  return (
    <ul
      className={`grid ${SKELETON_BOARD.columns} ${SKELETON_BOARD.rows} content-start gap-4`}
      aria-hidden
      data-testid="till-goods-skeleton"
    >
      {[0, 1, 2, 3, 4, 5].map((slot) => (
        <li key={slot} className="min-h-touch animate-pulse rounded-2xl bg-muted" />
      ))}
    </ul>
  );
}
